"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, TrendingUp, MapPin, Users, ShieldCheck, CircleCheck as CheckCircle2, Sparkles, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
export default function DermatologyHero() {
  const shouldReduceMotion = useReducedMotion();
  const metrics = [{
    label: "Organic Local Visibility",
    value: "+312%",
    detail: "Google Maps & Local Pack",
    icon: TrendingUp
  }, {
    label: "Direct Patient Enquiries",
    value: "+185%",
    detail: "Phone calls & appointment forms",
    icon: Users
  }, {
    label: "Top 3 Geo-Rankings",
    value: "42 Keywords",
    detail: "High-intent private skin treatments",
    icon: MapPin
  }];
  const projectDetails = [{
    label: "Client Type",
    value: "Private Dermatology & Aesthetic Clinic"
  }, {
    label: "Consultant Role",
    value: "Hands-on Local SEO Strategy & Implementation"
  }, {
    label: "Timeline",
    value: "6-Month Strategic Engagement"
  }, {
    label: "Core Focus",
    value: "Local Citations, Technical Fixes & High-Intent Landing Pages"
  }];
  const animateProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    animate: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    }
  };
  return <section id="dermatologycasestudyhero" data-nav="light" className="relative overflow-hidden bg-background py-12 md:py-20 lg:py-24 border-b border-border">
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-secondary/50 blur-3xl" />
        <div className="absolute top-1/2 -left-32 w-80 h-80 rounded-full bg-accent/10 blur-3xl" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div {...animateProps} transition={{
        duration: 0.4
      }} className="mb-6">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/HomePage">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator>
                <ChevronRight className="size-3.5" />
              </BreadcrumbSeparator>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/CaseStudiesPage">Case Studies</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator>
                <ChevronRight className="size-3.5" />
              </BreadcrumbSeparator>
              <BreadcrumbItem>
                <BreadcrumbPage>Dermatology Clinic SEO</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div {...animateProps} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-7 flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-medium text-xs">
                Local SEO Case Study
              </Badge>
              <Badge variant="outline" className="px-3 py-1 font-medium text-xs flex items-center gap-1.5">
                <ShieldCheck className="size-3.5 text-primary" />
                Verified Results
              </Badge>
              <Badge variant="outline" className="px-3 py-1 font-medium text-xs text-muted-foreground">
                Healthcare & Medical
              </Badge>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground leading-[1.15]">
              Scaling Local Patient Enquiries by +185% for a Private Dermatology Practice
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              When this specialist clinic approached me, their clinical reputation was unmatched, but their search presence was buried behind generic directories. I restructured their local SEO architecture, resolved critical schema issues, and built treatment-focused landing pages that turned local searches into private consultations.
            </p>

            <div className="p-4 rounded-lg bg-card border border-border flex items-start gap-3 shadow-xs">
              <Sparkles className="size-5 text-accent shrink-0 mt-0.5" />
              <p className="text-xs sm:text-sm text-foreground">
                <strong className="font-semibold text-primary">Delivered for this clinic:</strong> Full technical remediation, structured medical schema, dedicated treatment landing pages, and local citation cleanups that directly drove a +185% increase in patient bookings.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="rounded-md font-medium shadow-sm">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowUpRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md font-medium">
                <Link to="/CaseStudiesPage">Explore All Case Studies</Link>
              </Button>
            </div>
          </motion.div>

          <motion.div {...animateProps} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="lg:col-span-5">
            <Card className="border border-border bg-card shadow-lg overflow-hidden">
              <div className="bg-primary px-6 py-5 text-primary-foreground flex items-center justify-between">
                <div>
                  <span className="text-xs uppercase tracking-wider font-semibold opacity-80 block">Key Performance Snapshot</span>
                  <span className="text-lg font-bold font-default">Dermatology Practice Growth</span>
                </div>
                <div className="px-2.5 py-1 rounded-md bg-primary-foreground/15 text-primary-foreground text-xs font-medium">
                  Verified Data
                </div>
              </div>

              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-1 gap-4">
                  {metrics.map((metric, i) => {
                  const IconComponent = metric.icon;
                  return <div key={i} data-index={i} className="flex items-center gap-4 p-3.5 rounded-lg bg-background border border-border transition-colors hover:border-primary/40">
                        <div className="size-11 rounded-md bg-secondary flex items-center justify-center shrink-0">
                          <IconComponent className="size-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between">
                            <span className="text-sm font-medium text-muted-foreground">{metric.label}</span>
                            <span className="text-xl font-bold font-default text-primary">{metric.value}</span>
                          </div>
                          <span className="text-xs text-muted-foreground block truncate">{metric.detail}</span>
                        </div>
                      </div>;
                })}
                </div>

                <div className="border-t border-border pt-5 space-y-3">
                  <div className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Project Parameters
                  </div>
                  <div className="space-y-2">
                    {projectDetails.map((detail, i) => <div key={i} data-index={i} className="flex items-start justify-between text-xs gap-3">
                        <span className="text-muted-foreground shrink-0">{detail.label}:</span>
                        <span className="font-medium text-foreground text-right">{detail.value}</span>
                      </div>)}
                  </div>
                </div>

                <div className="pt-2">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted p-3 rounded-md">
                    <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                    <span>Focus: Sustainable Google Maps rankings with zero black-hat risks.</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <motion.div {...animateProps} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="mt-16 pt-8 border-t border-border grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div data-index={0} className="p-3 rounded-lg bg-card/60 border border-border">
            <span className="text-xs text-muted-foreground block">Step 01</span>
            <span className="text-sm font-semibold font-default text-foreground">Local Entity Audit</span>
          </div>
          <div data-index={1} className="p-3 rounded-lg bg-card/60 border border-border">
            <span className="text-xs text-muted-foreground block">Step 02</span>
            <span className="text-sm font-semibold font-default text-foreground">Treatment Landing Pages</span>
          </div>
          <div data-index={2} className="p-3 rounded-lg bg-card/60 border border-border">
            <span className="text-xs text-muted-foreground block">Step 03</span>
            <span className="text-sm font-semibold font-default text-foreground">Medical Schema & Citations</span>
          </div>
          <div data-index={3} className="p-3 rounded-lg bg-card/60 border border-border">
            <span className="text-xs text-muted-foreground block">Step 04</span>
            <span className="text-sm font-semibold font-default text-foreground">Sustainable Patient Growth</span>
          </div>
        </motion.div>
      </div>
    </section>;
}